import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string[];
  image?: string;
  url?: string;
  type?: string;
  /** Include WebSite + SearchAction JSON-LD (only on homepage) */
  includeWebsiteSchema?: boolean;
}

export const SEOHead = React.memo(React.forwardRef<HTMLDivElement, SEOHeadProps>(function SEOHead({
  title = 'Hostel Hunters - Find Your Perfect Student Accommodation in Ghana',
  description = 'Discover verified student hostels across Ghana. Book rooms, connect with roommates, and find your perfect accommodation near top universities.',
  keywords = ['Ghana hostels', 'student accommodation', 'university housing', 'hostel booking', 'roommate finder'],
  image = 'https://hostelhunters.com/hero-hostel.webp',
  url = window.location.href,
  type = 'website',
  includeWebsiteSchema = false
}, _ref) {
  const siteTitle = title.includes('Hostel Hunters') ? title : `${title} | Hostel Hunters`;

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": "https://hostelhunters.com/#website",
    "name": "Hostel Hunters",
    "alternateName": "HostelHunters",
    "url": "https://hostelhunters.com",
    "description": "Find your perfect student accommodation in Ghana. Browse verified hostels near top universities.",
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://hostelhunters.com/search?q={search_term_string}"
      },
      "query-input": "required name=search_term_string"
    }
  };

  const navigationSchema = {
    "@context": "https://schema.org",
    "@graph": [
      { "@type": "SiteNavigationElement", "name": "Find Hostels", "url": "https://hostelhunters.com/search" },
      { "@type": "SiteNavigationElement", "name": "Login", "url": "https://hostelhunters.com/auth" },
      { "@type": "SiteNavigationElement", "name": "Sign Up", "url": "https://hostelhunters.com/auth?mode=signup" },
      { "@type": "SiteNavigationElement", "name": "List Property", "url": "https://hostelhunters.com/list-property" },
      { "@type": "SiteNavigationElement", "name": "About", "url": "https://hostelhunters.com/about" },
      { "@type": "SiteNavigationElement", "name": "Contact", "url": "https://hostelhunters.com/contact" },
      { "@type": "SiteNavigationElement", "name": "FAQ", "url": "https://hostelhunters.com/faq" },
      { "@type": "SiteNavigationElement", "name": "Personality Quiz", "url": "https://hostelhunters.com/personality-quiz" }
    ]
  };
  
  return (
    <Helmet>
      <title>{siteTitle}</title>
      <meta name="title" content={siteTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords.join(', ')} />
      
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={siteTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content="Hostel Hunters" />
      
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={siteTitle} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />
      
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="author" content="Hostel Hunters" />
      <link rel="canonical" href={url} />

      {includeWebsiteSchema && (
        <>
          <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
          <script type="application/ld+json">{JSON.stringify(navigationSchema)}</script>
        </>
      )}
    </Helmet>
  );
}));

SEOHead.displayName = 'SEOHead';
